import axios, { type AxiosError, isAxiosError } from 'axios';

import { createConstantRetryStrategy, withRetries } from '@genially/commons';
import type { InteractiveQuestionBankResponse } from '@genially/contracts';
import type { BaseWidgetItemData } from '@genially/document-model';

import { config } from '../../bootstrap/application/config/config';
import { GeniallyIncompatibleError } from '../domain/errors/GeniallyIncompatibleError';
import type { PlayPostMessageInteractiveQuestion } from '../domain/types/PlayPostMessageInteractiveQuestion';

const client = axios.create({
  baseURL: `${config.urls.view}/api/view/`,
});

const getViewData = async (id: string): Promise<any> => {
  try {
    const response = await withRetries(
      // `fromApp=play` tells the view backend not to apply the "log in with a
      // Genially account" wall: in Play a genially-account genially must be
      // served like a regular one (Play has its own participant identity).
      () => client.get(`${id}`, { params: { fromApp: 'play' } }),
      createConstantRetryStrategy({
        wait: 2500,
        shouldRetryError: (error: AxiosError) => {
          if (error.code === 'ERR_BAD_REQUEST') {
            return false;
          }

          return true;
        },
      }),
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching view data:', error);
    throw error;
  }
};

const verifyGeniallyIsCompatible = async (id: string): Promise<void> => {
  try {
    const viewData = await getViewData(id);

    if (viewData.Genially.HasPassword) {
      throw GeniallyIncompatibleError.causeHasPassword();
    }
  } catch (error) {
    console.error('Error verifying that genially is compatible:', error);

    if (isAxiosError(error) && error.response?.status === 400) {
      throw GeniallyIncompatibleError.causeIsDraft();
    }

    if (isAxiosError(error) && error.response?.status === 401) {
      throw GeniallyIncompatibleError.causeIsRestricted();
    }

    if (isAxiosError(error) && error.response?.status === 403) {
      throw GeniallyIncompatibleError.causeIsForbidden();
    }

    throw error;
  }
};

const getInitialSlideId = async (id: string): Promise<string> => {
  try {
    const viewData = await getViewData(id);
    return (
      viewData.Slides.find((slide: { Order: number }) => slide.Order === 1)?.Id ?? ''
    );
  } catch (error) {
    console.error('Error fetching initial slide ID:', error);
    throw error;
  }
};

const getGeniallyName = async (id: string): Promise<string> => {
  try {
    const viewData = await getViewData(id);
    return viewData.Genially.Name;
  } catch (error) {
    console.error('Error fetching genially name:', error);
    throw error;
  }
};

const getGeniallyDescription = async (id: string): Promise<string> => {
  try {
    const viewData = await getViewData(id);
    return viewData.Genially.Description;
  } catch (error) {
    console.error('Error fetching genially name:', error);
    throw error;
  }
};

const getGeniallyImageRenderUrl = async (id: string): Promise<string> => {
  try {
    const viewData = await getViewData(id);
    return viewData.Genially.ImageRender;
  } catch (error) {
    console.error('Error fetching genially image render URL:', error);
    throw error;
  }
};

const getInteractiveQuestionBank = async (
  bankId: string,
): Promise<InteractiveQuestionBankResponse | null> => {
  try {
    const response = await client.get(`interactive-question-bank/${bankId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching interactive question bank:', error);
    return null;
  }
};

const getInteractiveQuestions = async (
  id: string,
): Promise<PlayPostMessageInteractiveQuestion[]> => {
  try {
    const viewData = await getViewData(id);
    const trackableWidgets = (viewData.BaseWidgets ?? []).filter(
      (widget: BaseWidgetItemData) => widget.script?.tracking?.isTrackable === true,
    );
    return [...viewData.Activities, ...trackableWidgets];
  } catch (error) {
    console.error('Error fetching interactive questions:', error);
    throw error;
  }
};

export const ViewApiService = {
  getViewData,
  verifyGeniallyIsCompatible,
  getInitialSlideId,
  getGeniallyName,
  getGeniallyDescription,
  getGeniallyImageRenderUrl,
  getInteractiveQuestionBank,
  getInteractiveQuestions,
};
